import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

import type { FormatMessages } from "@skilitics/localization";
import { useDynamicFormatString, useFormatMessage } from "@skilitics/localization";
import type { NodeRole } from "@skilitics/nautilus-core";
import {
  assert,
  getNodeSlotIdSelector,
  type ImportTarget,
  type PortableDesignerNodeInContext,
  type SlotId,
} from "@skilitics/nautilus-core-designer-data";

import { useContentHostId } from "../backend/index.ts";
import type { LayoutContentContext } from "../composable/layoutable/LayoutContentContext.tsx";
import { createNodeSelectionCriteriaFilter, type NodeSelectionCriteria } from "../library/index.ts";
import { collectBoundSlotRefs } from "../nodes/dereferenced/collectBoundSlotRefs.ts";
import { getNodeRoleLabel } from "../state/getNodeRoleLabel.ts";
import { useGetByIdMultilingual } from "../state/selectorWrappers/useGetByIdMultilingual.ts";
import {
  useResolveNodeLinkagesBlockingMoveToSlot,
  type CanImportResult,
} from "../state/useResolveNodeLinkagesBlockingMoveToSlot.ts";

// Passed only by call sites whose paste/import lands in that layoutable's layout.
export type ImportTargetLayout = Pick<LayoutContentContext, "canBindSlotRef" | "layoutRootId">;

type CanImportHereParams = {
  target: ImportTarget;
  formatStringOverrides?: Partial<FormatMessages>;
} & Pick<CanImportParams, "criteria" | "targetLayout">;

export const useCanImportHere = ({ target, criteria, targetLayout, formatStringOverrides }: CanImportHereParams) => {
  const getSlotIdById = useSelector(getNodeSlotIdSelector);
  const hostId = useContentHostId();
  const canImport = useCanImport(formatStringOverrides);
  const slotId = "slotId" in target ? target.slotId : getSlotIdById(target.slotResidentNodeId);
  // A slot-resident target is a paste-and-replace.
  const replacedNodeId = "slotResidentNodeId" in target ? target.slotResidentNodeId : undefined;

  return useCallback(
    (subject: PortableDesignerNodeInContext): CanImportResult =>
      canImport({ criteria, target: { hostId, slotId }, subject, targetLayout, replacedNodeId }),
    [canImport, criteria, hostId, replacedNodeId, slotId, targetLayout],
  );
};

type CanImportAtTargetParams = {
  targetSlotId?: SlotId;
} & Pick<CanImportParams, "criteria" | "subject" | "targetLayout">;

export const useCanImportAtTarget = () => {
  const hostId = useContentHostId();
  const canImport = useCanImport();
  return useCallback(
    ({ criteria, subject, targetSlotId, targetLayout }: CanImportAtTargetParams): CanImportResult =>
      canImport({ criteria, target: { hostId, slotId: targetSlotId }, subject: subject, targetLayout }),
    [canImport, hostId],
  );
};

type CanImportParams = {
  target: { hostId: string; slotId?: SlotId };
  criteria: NodeSelectionCriteria;
  subject: PortableDesignerNodeInContext;
  targetLayout?: ImportTargetLayout;
  replacedNodeId?: number;
};

const useCanImport = (formatStringOverrides?: Partial<FormatMessages>) => {
  const mergedFormatStrings = useMemo(() => ({ ...formatStrings, ...formatStringOverrides }), [formatStringOverrides]);
  const formatMessage = useFormatMessage(mergedFormatStrings);
  const formatDynamic = useDynamicFormatString();
  const getById = useGetByIdMultilingual();
  const resolveSnaggedNodeLinkages = useResolveNodeLinkagesBlockingMoveToSlot();
  return useCallback(
    ({
      target,
      criteria,
      subject: { source, node, linkages },
      targetLayout,
      replacedNodeId,
    }: CanImportParams): CanImportResult => {
      // TODO: check versions match for each node in subtree
      if (source.origin !== window.location.origin) {
        return {
          value: false,
          message: formatMessage("originDoesNotMatch", { origin: source.origin }),
        };
      }

      if (criteria) {
        const criteriaFilter = createNodeSelectionCriteriaFilter(criteria);

        // A friendly name for what this slot accepts, e.g. "Frame", "Overlay", "Element".
        const acceptedRole =
          criteria.role ??
          criteria.include?.find((entry): entry is { role: NodeRole } => "role" in entry && !!entry.role)?.role;
        const accepted = getNodeRoleLabel(acceptedRole as NodeRole | undefined) ?? formatMessage("genericContentName");

        if (criteria.typeId && !criteriaFilter({ typeId: node.typeId })) {
          return { value: false, message: formatMessage("resultInvalidType", { accepted }) };
        }
        if (criteria.role && !criteriaFilter({ role: node.role })) {
          return { value: false, message: formatMessage("resultInvalidRole", { accepted }) };
        }
        if (!criteriaFilter(node)) {
          return { value: false, message: formatMessage("resultInvalidNodeSelectionCriteria", { accepted }) };
        }
      }

      if (targetLayout) {
        // Subject-subtree slot refs bind to the target layoutable (dereferenced nodes fulfil their own).
        const { canBindSlotRef, layoutRootId } = targetLayout;
        const subjectSlotRefs = collectBoundSlotRefs(node);
        if (subjectSlotRefs.length > 0) {
          if (!canBindSlotRef) {
            return {
              value: false,
              message: formatMessage("resultSlotRefNotBindable", { name: subjectSlotRefs[0].name }),
            };
          }
          const alreadyBoundSlotRefs =
            layoutRootId === undefined
              ? []
              : collectBoundSlotRefs(getById(layoutRootId), { excludeSubtreeOfId: replacedNodeId });
          for (const slotRef of subjectSlotRefs) {
            const can = canBindSlotRef(slotRef, { alreadyBoundSlotRefs });
            if (!can.value) {
              return { value: false, message: formatDynamic(can.reason) };
            }
          }
        }
      }

      if (target.hostId === source.hostId) {
        assert(target.slotId);
        if (source.action === "cut") {
          if (target.slotId.parentId === source.slotId.parentId && target.slotId.name === source.slotId.name) {
            return {
              value: false,
              message: formatMessage("resultSameParent"),
            };
          }
          const { dereferencedNodes } = linkages ?? {};
          if (dereferencedNodes && dereferencedNodes.length > 0) {
            return {
              value: false,
              message: formatMessage("resultContainsDereferenced"),
            };
          }
          const { configReferences, associations } = resolveSnaggedNodeLinkages(node.id, target.slotId);
          if (associations.length > 0 || configReferences.length > 0) {
            return {
              value: false,
              configReferences,
              associations,
            };
          }
        }
      } else if (source.action === "cut") {
        return {
          value: false,
          message: formatMessage("cutFromOtherModule"),
        };
      }

      return true;
    },
    [formatDynamic, formatMessage, getById, resolveSnaggedNodeLinkages],
  );
};

const formatStrings = {
  resultSameParent: {
    en: "This is already here. Drag it to reorder instead.",
  },
  resultInvalidType: {
    en: "Only {accepted} content can be pasted here.",
  },
  resultInvalidRole: {
    en: "Only {accepted} content can be pasted here.",
  },
  originDoesNotMatch: {
    en: "This was copied from a different site ({origin}), so it can't be pasted here.",
  },
  cutFromOtherModule: {
    en: "You can't move this between modules. Copy it instead.",
  },
  resultContainsDereferenced: {
    en: "You can't move a layout that contains content. Copy it instead.",
  },
  resultSlotRefNotBindable: {
    // this is a fallback as most layoutables will do their own canBindSlotRef implementation and hence messaging
    en: 'This content appears to be from a different type of layout. This location does not support "{name}" placeholders.',
  },
  resultInvalidNodeSelectionCriteria: {
    en: "Only {accepted} content can be pasted here.",
  },
  genericContentName: {
    en: "this kind of",
  },
} satisfies FormatMessages;
